/**
 * Chat Landing Page — Mission discovery with search
 *
 * Every conversation in the chat-ui happens within a mission context.
 * This page helps users discover and select a mission:
 * - Search bar (auto-focused) for finding missions by name
 * - "Active now" section — running + HITL missions that need attention
 * - "Recent" section — last missions the user interacted with
 * - Search results replace sections when user types
 *
 * Deep link support:
 *   /chat?mission={id}             -> pre-select mission
 *   /chat?mission={id}&autostart   -> auto-launch mission
 */

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Target, ExternalLink, Play, Zap } from 'lucide-react'
import { cn, Badge, Button, Text, SearchInput } from '@agion/ui'
import { useChatLandingModel } from '../hooks/useChatLandingModel'
import AgionLogo from '../assets/Agion-512-transparent.png'

// ============================================================================
// Helper: Format relative time
// ============================================================================

function formatRelativeTime(date: Date | string | undefined, t: (key: string, fallback: string) => string): string {
  if (!date) return ''
  const now = new Date()
  const then = new Date(date)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return t('timeAgo.justNow', 'just now')
  if (diffMins < 60) return t('timeAgo.minutesAgo', '{{count}}m ago').replace('{{count}}', String(diffMins))
  if (diffHours < 24) return t('timeAgo.hoursAgo', '{{count}}h ago').replace('{{count}}', String(diffHours))
  return t('timeAgo.daysAgo', '{{count}}d ago').replace('{{count}}', String(diffDays))
}

// ============================================================================
// MissionRow Component — compact list item
// ============================================================================

interface MissionRowProps {
  mission: {
    id: string
    name: string
    description?: string
    status?: string
    state?: string
    updatedAt?: string
    last_activity?: string
  }
  isRunning?: boolean
  hasHITL?: boolean
  /** A draft found by search (agion/Agion#2873): marked, and opened rather than started, as in the sidebar. */
  isDraft?: boolean
  onSelect: (missionId: string) => void
  onStart: (missionId: string) => void
}

function MissionRow({ mission, isRunning, hasHITL, isDraft, onSelect, onStart }: MissionRowProps) {
  const { t } = useTranslation('chat')
  const lastActivity = mission.updatedAt || mission.last_activity

  return (
    <div className="group">
      <Button
        variant="ghost"
        onClick={() => onSelect(mission.id)}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3.5 h-auto text-left',
          'rounded-lg transition-colors',
          'hover:bg-slate-50 dark:hover:bg-slate-800/50'
        )}
        aria-label={t('openMission', 'Open mission {{name}}', { name: mission.name })}
        data-testid={`mission-open-${mission.id}`}
      >
        {/* Status dot */}
        <span className="flex-shrink-0 w-5 flex items-center justify-center">
          {hasHITL ? (
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          ) : isRunning ? (
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          ) : (
            <Target className="h-4 w-4 text-slate-400 dark:text-slate-500" />
          )}
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <Text weight="medium" size="sm" className="truncate block">
            {mission.name}
          </Text>
          {mission.description && (
            <Text size="xs" color="muted" className="truncate block">
              {mission.description}
            </Text>
          )}
        </div>

        {/* Right side: status or time */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {isDraft && (
            <Badge variant="muted" size="sm" data-testid={`mission-draft-${mission.id}`}>
              {t('landing.draft', 'Draft')}
            </Badge>
          )}
          {hasHITL && (
            <Badge variant="warning" size="sm">
              {t('status.needsInput', 'Needs input')}
            </Badge>
          )}
          {isRunning && !hasHITL && (
            <Badge variant="success" size="sm">
              {t('status.running', 'Running')}
            </Badge>
          )}
          {!isRunning && !hasHITL && lastActivity && (
            <span className="text-xs text-slate-400 dark:text-slate-500 tabular-nums">
              {formatRelativeTime(lastActivity, t)}
            </span>
          )}
        </div>
      </Button>
      {/* Start New — quick-launch a fresh execution */}
      {!isRunning && !hasHITL && !isDraft && (
        <Button
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); onStart(mission.id) }}
          className={cn(
            'w-full flex items-center gap-3 px-4 py-1.5 h-auto text-left',
            'rounded-lg transition-colors',
            'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
          )}
          aria-label={t('startMission', 'Start new run for {{name}}', { name: mission.name })}
          data-testid={`mission-start-${mission.id}`}
        >
          <span className="flex-shrink-0 w-5 flex items-center justify-center">
            <Play className="h-3 w-3" />
          </span>
          <Text size="xs" weight="medium">
            {t('startNew', 'Start New')}
          </Text>
        </Button>
      )}
    </div>
  )
}

// ============================================================================
// Section Header
// ============================================================================

function SectionHeader({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2">
      <span className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
        {children}
      </span>
      {count !== undefined && count > 0 && (
        <Badge variant="default" size="sm">{count}</Badge>
      )}
    </div>
  )
}

// ============================================================================
// ChatLandingPage Component
// ============================================================================

export const ChatLandingPage: React.FC = () => {
  const { t } = useTranslation('chat')
  const searchRef = useRef<HTMLInputElement>(null)
  const {
    isLoading,
    missionStates,
    searchTerm,
    setSearchTerm,
    isSearching,
    activeMissions,
    runningMissions,
    recentMissions,
    searchResults,
    handleSelectMission,
    handleStartMission,
    handleGoToAdmin,
  } = useChatLandingModel()

  // Auto-focus search on mount
  useEffect(() => {
    setTimeout(() => searchRef.current?.focus(), 100)
  }, [])

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-950">
      <div className="flex-1 flex flex-col items-center px-6 py-12 overflow-auto">
        <div className="max-w-2xl mx-auto w-full">
          {/* Logo + Search */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center mb-8"
          >
            <img
              src={AgionLogo}
              alt="Agion"
              className="h-16 w-16 object-contain mb-6"
            />
            <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100 text-center mb-6">
              {t('landing.title', 'What would you like to work on?')}
            </h1>

            {/* Search bar */}
            <div className="w-full max-w-lg">
              <SearchInput
                ref={searchRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={() => setSearchTerm('')}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setSearchTerm('')
                    searchRef.current?.blur()
                  }
                  // Enter selects the first result
                  if (e.key === 'Enter' && isSearching && searchResults.length > 0) {
                    handleSelectMission(searchResults[0]!.id)
                  }
                }}
                placeholder={t('landing.searchPlaceholder', 'Search missions...')}
                size="lg"
              />
            </div>
          </motion.div>

          {/* Content area */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            {isSearching ? (
              /* ============================================ */
              /* Search Results                               */
              /* ============================================ */
              <div>
                <SectionHeader count={searchResults.length}>
                  {t('landing.searchResults', 'Results')}
                </SectionHeader>
                {searchResults.length > 0 ? (
                  <div className="space-y-0.5">
                    {searchResults.map((mission) => {
                      const state = missionStates.get(mission.id)
                      return (
                        <MissionRow
                          key={mission.id}
                          mission={mission}
                          isRunning={state?.isRunning}
                          hasHITL={state?.hasActiveHITL}
                          isDraft={mission.isDraft}
                          onSelect={handleSelectMission}
                          onStart={handleStartMission}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center">
                    <Text size="sm" color="muted">
                      {t('landing.noResults', 'No missions match your search')}
                    </Text>
                  </div>
                )}
              </div>
            ) : activeMissions.length > 0 ? (
              /* ============================================ */
              /* Active Now + Recent                          */
              /* ============================================ */
              <div className="space-y-4">
                {/* Active Now — running or HITL */}
                {runningMissions.length > 0 && (
                  <div>
                    <SectionHeader count={runningMissions.length}>
                      <span className="flex items-center gap-1.5">
                        <Zap className="h-3 w-3" />
                        {t('landing.activeNow', 'Active now')}
                      </span>
                    </SectionHeader>
                    <div className="space-y-0.5">
                      {runningMissions.map((mission) => {
                        const state = missionStates.get(mission.id)
                        return (
                          <MissionRow
                            key={mission.id}
                            mission={mission}
                            isRunning={state?.isRunning}
                            hasHITL={state?.hasActiveHITL}
                            onSelect={handleSelectMission}
                            onStart={handleStartMission}
                          />
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Recent missions */}
                {recentMissions.length > 0 && (
                  <div>
                    <SectionHeader>
                      {t('landing.recentMissions', 'Recent')}
                    </SectionHeader>
                    <div className="space-y-0.5">
                      {recentMissions.map((mission) => (
                        <MissionRow
                          key={mission.id}
                          mission={mission}
                          onSelect={handleSelectMission}
                          onStart={handleStartMission}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : !isLoading ? (
              /* ============================================ */
              /* No missions                                  */
              /* ============================================ */
              <div className="px-4 py-12 text-center">
                <Text size="sm" color="muted" className="mb-4 block">
                  {t('landing.noMissionsDesc', 'Create a mission in the admin panel to get started')}
                </Text>
                <Button
                  variant="outline"
                  onClick={handleGoToAdmin}
                  className="gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t('landing.goToAdmin', 'Open Admin Panel')}
                </Button>
              </div>
            ) : null}
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default ChatLandingPage
